import React from 'react';
import { graphql } from 'gatsby';

import Layout from '../components/layout';
import SEO from '../components/SEO/SEO';
import ExitIntentTest from '../components/ExitIntent/ExitIntentTest';
import ChiliPiper from '../components/SEO/ChiliPiper';
import AnnouncementBar from '../components/AnnouncementBar';

import componentGenerator from '../utils/componentGenerator';
import resolveAnnouncementBar from '../utils/resolveAnnouncementBar';
import { generateBreadcrumbSchemaMarkup } from '../utils/generateSchemaMarkup';

const PageTemplate = ({ data, location, pageContext }) => {
  const { slug } = pageContext;

  const page = data?.datoCmsWebsitePage;
  const components = page?.components;

  const announcementBar = resolveAnnouncementBar({
    globalAnnouncementBar: data?.datoCmsGlobalAnnouncementBarComponent,
    pageAnnouncementBar: page?.announcementBar,
    slug,
  });

  return (
    <Layout>
      <ExitIntentTest />
      {announcementBar && <AnnouncementBar {...announcementBar} />}
      {componentGenerator({
        slug,
        location,
        components,
        postTypes: {
          blog: data?.allDatoCmsBlogPost?.nodes,
          customers: data?.allDatoCmsCustomerSuccessStory?.nodes,
          downloads: data?.allDatoCmsDownload?.nodes,
          events: data?.allDatoCmsEventPost?.nodes,
          learning: data?.allDatoCmsLearningPost?.nodes,
          integrations: data?.allDatoCmsIntegration?.nodes,
          checklists: data?.allDatoCmsTemplateCollection?.nodes,
          'product-release': data?.allProductRelease?.nodes,
          reviews: data?.allDatoCmsCustomerReview?.nodes,
          partnerships: data?.allDatoCmsPartnership?.nodes,
          press: data?.allDatoCmsPressRelease?.nodes,
          resources: {
            events: {
              totalCount: data?.resourceListingEvents?.totalCount,
              posts: data?.resourceListingEvents?.nodes,
            },
            learningPosts: {
              totalCount: data?.resourceListingLearningPosts?.totalCount,
              posts: data?.resourceListingLearningPosts?.nodes,
            },
            blogPosts: {
              totalCount: data?.resourceListingBlogPosts?.totalCount,
              posts: data?.resourceListingBlogPosts?.nodes,
            },
            podcasts: {
              totalCount: data?.resourceListingPodcasts?.totalCount,
              posts: data?.resourceListingPodcasts?.nodes,
            },
            downloads: {
              totalCount: data?.resourceListingDownloads?.totalCount,
              posts: data?.resourceListingDownloads?.nodes,
            },
            templates: {
              totalCount: data?.resourceListingTemplates?.totalCount,
              posts: data?.resourceListingTemplates?.nodes,
            },
          },
        },
        pageMeta: page,
      })}
      <ChiliPiper pathname={location?.pathname} />
    </Layout>
  );
};

export default PageTemplate;

export const Head = ({ data, location }) => {
  const { datoCmsWebsitePage: page } = data;

  const breadcrumbSchema = generateBreadcrumbSchemaMarkup(location?.pathname);

  return (
    <>
      <SEO
        title={page?.metaTags?.title || page?.internalName}
        description={page?.metaTags?.description}
        indexable={page?.pageMeta?.indexable || true}
        imgUrl={page?.metaTags?.image?.url}
        pathname={location?.pathname}
        schema={breadcrumbSchema}
      />
    </>
  );
};

export const PageQuery = graphql`
  query(
    $id: String!
    $language: String!
    $queryBlog: Boolean!
    $queryCustomerStories: Boolean!
    $queryDownloads: Boolean!
    $queryEvents: Boolean!
    $queryLearning: Boolean!
    $queryIntegrations: Boolean!
    $queryTemplates: Boolean!
    $queryProductRelease: Boolean!
    $queryReviews: Boolean!
    $queryResourceListing: Boolean!
    $queryPartnerships: Boolean!
    $queryPressReleases: Boolean!
  ) {
    datoCmsGlobalAnnouncementBarComponent {
      ...datoCmsGlobalAnnouncementBarComponent
    }
    # Query the page
    datoCmsWebsitePage(id: { eq: $id }) {
      ...datoCmsWebsitePage
    }
    # Query resources for resource listing:
    # Events (8)
    # Learning Center (11)
    # Blog (7)
    # Podcasts (10)
    # Downloads (9)
    # Templates (7)
    resourceListingEvents: allDatoCmsEventPost(
      sort: { meta: { updatedAt: DESC } }
      limit: 8
    ) @include(if: $queryResourceListing) {
      nodes {
        ...datoCmsEventPost
      }
      totalCount
    }
    resourceListingLearningPosts: allDatoCmsLearningPost(
      sort: { meta: { publishedAt: DESC } }
      limit: 11
    ) @include(if: $queryResourceListing) {
      nodes {
        ...datoCmsLearningPost
      }
      totalCount
    }
    resourceListingBlogPosts: allDatoCmsBlogPost(
      sort: { meta: { createdAt: DESC } }
      limit: 7
    ) @include(if: $queryResourceListing) {
      nodes {
        ...datoCmsBlogPost
      }
      totalCount
    }
    resourceListingPodcasts: allDatoCmsPodcast(
      sort: { meta: { updatedAt: DESC } }
      limit: 10
    ) @include(if: $queryResourceListing) {
      nodes {
        ...datoCmsPodcast
      }
      totalCount
    }
    resourceListingDownloads: allDatoCmsDownload(
      sort: { meta: { publishedAt: DESC } }
      limit: 9
    ) @include(if: $queryResourceListing) {
      nodes {
        ...downloadPostListingQuery
      }
      totalCount
    }
    resourceListingTemplates: allDatoCmsTemplateCollection(
      sort: { meta: { publishedAt: DESC } }
      limit: 7
    ) @include(if: $queryResourceListing) {
      nodes {
        ...datoCmsTemplateCollection
      }
      totalCount
    }

    # Query all blog posts
    allDatoCmsBlogPost(sort: { meta: { createdAt: DESC } })
      @include(if: $queryBlog) {
      nodes {
        ...datoCmsBlogPost
      }
    }
    # Query all product releases
    allProductRelease: allDatoCmsProductRelease(
      sort: { meta: { createdAt: DESC } }
    ) @include(if: $queryProductRelease) {
      nodes {
        id
        slug
        internalName(locale: $language, fallbackLocales: "en")
        indexable
        showTableOfContents
        publishDate(formatString: "LL")
        category {
          slug
          internalName(locale: $language, fallbackLocales: "en")
          color
        }
        meta {
          publishedAt(formatString: "MMMM DD, YYYY")
        }
        metaTags(locale: $language, fallbackLocales: "en") {
          title
          description
          image {
            ...datoCmsAsset
          }
        }
        author {
          ...datoCmsPerson
        }
        content(locale: $language, fallbackLocales: "en") {
          ... on DatoCmsRichText {
            id
            content
          }
        }
      }
    }
    # Query all customer success stories
    allDatoCmsCustomerSuccessStory(sort: { meta: { createdAt: DESC } })
      @include(if: $queryCustomerStories) {
      nodes {
        ...datoCmsCustomerSuccessStory
      }
    }
    # Query all downloads
    allDatoCmsDownload(sort: { meta: { publishedAt: DESC } })
      @include(if: $queryDownloads) {
      nodes {
        ...downloadPostListingQuery
      }
    }
    # Query all events
    allDatoCmsEventPost(sort: { meta: { updatedAt: DESC } })
      @include(if: $queryEvents) {
      nodes {
        ...datoCmsEventPost
      }
    }
    # Query all learning posts
    allDatoCmsLearningPost(sort: { meta: { publishedAt: DESC } })
      @include(if: $queryLearning) {
      nodes {
        ...datoCmsLearningPost
      }
    }
    # Query all integrations
    allDatoCmsIntegration(sort: { internalName: ASC })
      @include(if: $queryIntegrations) {
      totalCount
      nodes {
        ...datoCmsIntegration
      }
    }
    # Query all templates
    allDatoCmsTemplateCollection(sort: { meta: { publishedAt: DESC } })
      @include(if: $queryTemplates) {
      nodes {
        ...datoCmsTemplateCollection
      }
    }
    # Query all customer reviews
    allDatoCmsCustomerReview(sort: { meta: { createdAt: DESC } })
      @include(if: $queryReviews) {
      totalCount
      nodes {
        ...datoCmsCustomerReview
      }
    }
    # Query all Partnerships
    allDatoCmsPartnership(sort: { meta: { createdAt: DESC } })
      @include(if: $queryPartnerships) {
      totalCount
      nodes {
        ...datoCmsPartnership
      }
    }
    # Query all press releases
    allDatoCmsPressRelease(sort: { meta: { createdAt: DESC } })
      @include(if: $queryPressReleases) {
      totalCount
      nodes {
        ...datoCmsPressRelease
      }
    }
    # Query all locales
    locales: allLocale(
      filter: { ns: { in: ["index"] }, language: { eq: $language } }
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
